import * as THREE from 'three';
import { Pulse, Spin } from '../animated';
import { CLASSICAL, MODERN, translucent } from '../materials';
import { Beam, levelEntry, type ModelProps, Pole, pyramid } from '../parts';

const { WOOD, DARK_WOOD, STONE, WALL_STONE, BRASS } = CLASSICAL;
const { CONCRETE, DARK_CONCRETE, STEEL, GUNMETAL, GLASS, DISH, RED_LIGHT } = MODERN;

const G = {
  footing: new THREE.BoxGeometry(0.07, 0.04, 0.07),
  deck: new THREE.BoxGeometry(0.3, 0.03, 0.3),
  rail: new THREE.BoxGeometry(0.3, 0.016, 0.016),
  roof: pyramid(0.24, 0.16),
  rung: new THREE.BoxGeometry(0.07, 0.01, 0.01),
  // L2: a round stone tower with a crenellated top and a conical cap.
  plinth: new THREE.CylinderGeometry(0.2, 0.22, 0.06, 10),
  shaft: new THREE.CylinderGeometry(0.13, 0.16, 0.5, 10),
  parapet: new THREE.CylinderGeometry(0.17, 0.15, 0.05, 10),
  merlon: new THREE.BoxGeometry(0.045, 0.05, 0.035),
  cap: new THREE.ConeGeometry(0.1, 0.16, 10),
  slit: new THREE.BoxGeometry(0.025, 0.07, 0.01),
  door: new THREE.BoxGeometry(0.07, 0.1, 0.01),
  tube: new THREE.CylinderGeometry(0.014, 0.022, 0.16, 8).rotateX(Math.PI / 2),
  lens: new THREE.CylinderGeometry(0.024, 0.024, 0.008, 8).rotateX(Math.PI / 2),
  // L3: a steel lattice mast on a concrete pad with a sensor head.
  pad: new THREE.CylinderGeometry(0.3, 0.32, 0.04, 6),
  hut: new THREE.BoxGeometry(0.14, 0.09, 0.11),
  head: new THREE.CylinderGeometry(0.07, 0.07, 0.05, 8),
  band: new THREE.CylinderGeometry(0.074, 0.074, 0.015, 8),
  radar: new THREE.BoxGeometry(0.24, 0.035, 0.012),
  camera: new THREE.BoxGeometry(0.045, 0.04, 0.07),
  cameraLens: new THREE.CylinderGeometry(0.014, 0.014, 0.01, 8).rotateX(Math.PI / 2),
  beacon: new THREE.SphereGeometry(0.022, 8, 6),
  halo: new THREE.SphereGeometry(0.045, 8, 6),
};

/** Timber tower: legs splay from `FOOT` at the ground to `TOP` under the deck. */
const TIMBER = { foot: 0.15, top: 0.1, deck: 0.48, roof: 0.66 };
const STONE_TOP = 0.56;
const MAST = { foot: 0.17, top: 0.035, height: 0.82 };
const RADAR_SPEED = 1.1; // radians per second
const BLINK_SPEED = 3; // radians per second
const HALO_OPACITY = 0.3;

const CORNERS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
] as const;

/**
 * Classical: a timber tower on stone footings, four splayed legs X-braced on
 * each side, a ladder up the front, and a railed lookout deck under a
 * team-coloured pyramid roof on corner posts.
 */
function TimberTower({ accent }: ModelProps) {
  const { foot, top, deck, roof } = TIMBER;
  const braceY = [0.06, deck / 2];
  return (
    <>
      {CORNERS.map(([sx, sz]) => (
        <group key={`${sx}:${sz}`}>
          <mesh geometry={G.footing} material={STONE} position={[sx * foot, 0.02, sz * foot]} />
          <Beam
            from={[sx * foot, 0.04, sz * foot]}
            to={[sx * top, deck, sz * top]}
            material={WOOD}
            thickness={0.03}
          />
          {/* Corner post holding the roof. */}
          <Beam
            from={[sx * 0.13, deck, sz * 0.13]}
            to={[sx * 0.13, roof, sz * 0.13]}
            material={DARK_WOOD}
            thickness={0.018}
          />
        </group>
      ))}
      {/* X-bracing on each side, one cross per storey. */}
      {[0, Math.PI / 2, Math.PI, -Math.PI / 2].map((a) => (
        <group key={a} rotation={[0, a, 0]}>
          {braceY.map((y0) => {
            const y1 = y0 + deck / 2 - 0.06;
            const w0 = foot - ((foot - top) * y0) / deck;
            const w1 = foot - ((foot - top) * y1) / deck;
            return (
              <group key={y0}>
                <Beam
                  from={[-w0, y0, w0]}
                  to={[w1, y1, w1]}
                  material={DARK_WOOD}
                  thickness={0.014}
                />
                <Beam
                  from={[w0, y0, w0]}
                  to={[-w1, y1, w1]}
                  material={DARK_WOOD}
                  thickness={0.014}
                />
              </group>
            );
          })}
        </group>
      ))}
      {/* Ladder up the front. */}
      <group position={[0, 0, foot + 0.04]} rotation={[-0.12, 0, 0]}>
        {[-0.035, 0.035].map((x) => (
          <Pole key={x} length={deck} position={[x, deck / 2, 0]} material={DARK_WOOD} />
        ))}
        {[0.08, 0.17, 0.26, 0.35, 0.44].map((y) => (
          <mesh key={y} geometry={G.rung} material={WOOD} position={[0, y, 0]} />
        ))}
      </group>
      <mesh geometry={G.deck} material={WOOD} position={[0, deck, 0]} />
      {[0, Math.PI / 2, Math.PI, -Math.PI / 2].map((a) => (
        <mesh
          key={a}
          geometry={G.rail}
          material={DARK_WOOD}
          position={[0.14 * Math.sin(a), deck + 0.07, 0.14 * Math.cos(a)]}
          rotation={[0, a, 0]}
        />
      ))}
      <mesh geometry={G.roof} material={accent} position={[0, roof + 0.08, 0]} />
    </>
  );
}

/**
 * L2: a round stone tower with arrow slits and a door, a crenellated parapet
 * with a team-coloured conical cap over the stair, and a brass telescope on a
 * tripod at the parapet, trained out past the front edge.
 */
function StoneTower({ accent }: ModelProps) {
  const merlons = Array.from({ length: 8 }, (_, i) => (i * Math.PI * 2) / 8);
  return (
    <>
      <mesh geometry={G.plinth} material={WALL_STONE} position={[0, 0.03, 0]} />
      <mesh geometry={G.shaft} material={STONE} position={[0, 0.06 + 0.25, 0]} />
      <mesh geometry={G.door} material={DARK_WOOD} position={[0, 0.11, 0.155]} />
      {[0.25, 0.42].map((y) => (
        <mesh key={y} geometry={G.slit} material={DARK_WOOD} position={[0, y, 0.145]} />
      ))}
      <mesh geometry={G.parapet} material={WALL_STONE} position={[0, STONE_TOP + 0.025, 0]} />
      {merlons.map((a) => (
        <mesh
          key={a}
          geometry={G.merlon}
          material={WALL_STONE}
          position={[0.15 * Math.sin(a), STONE_TOP + 0.075, 0.15 * Math.cos(a)]}
          rotation={[0, a, 0]}
        />
      ))}
      {/* Stair turret cap at the back. */}
      <group position={[-0.04, STONE_TOP + 0.05, -0.05]}>
        <mesh geometry={G.cap} material={accent} position={[0, 0.08, 0]} />
      </group>
      {/* Brass telescope on a tripod, aimed out over the front. */}
      <group position={[0.05, STONE_TOP + 0.05, 0.06]}>
        {[0, (Math.PI * 2) / 3, (Math.PI * 4) / 3].map((a) => (
          <Beam
            key={a}
            from={[0.035 * Math.sin(a), 0, 0.035 * Math.cos(a)]}
            to={[0, 0.07, 0]}
            material={DARK_WOOD}
            thickness={0.008}
          />
        ))}
        <group position={[0, 0.08, 0]} rotation={[-0.35, 0.3, 0]}>
          <mesh geometry={G.tube} material={BRASS} />
          <mesh geometry={G.lens} material={GLASS} position={[0, 0, 0.082]} />
        </group>
      </group>
    </>
  );
}

/**
 * L3: a three-legged steel lattice mast on a concrete pad with an equipment
 * hut, topped by a team-banded sensor head carrying a turning radar bar and a
 * camera, and a blinking glow light at the very top.
 */
function LatticeMast({ accent, glow }: ModelProps) {
  const { foot, top, height } = MAST;
  const legs = [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3];
  const at = (a: number, r: number, y: number): [number, number, number] => [
    r * Math.sin(a),
    y,
    r * Math.cos(a),
  ];
  const width = (y: number) => foot - ((foot - top) * y) / height;
  // Lattice panels: zig-zag bracing between each pair of legs.
  const steps = [0.04, 0.2, 0.34, 0.47, 0.58, 0.68, 0.76, height];
  const head = height + 0.02;
  return (
    <>
      <mesh geometry={G.pad} material={CONCRETE} position={[0, 0.02, 0]} />
      <group position={[0.2, 0.04, 0.12]} rotation={[0, -0.5, 0]}>
        <mesh geometry={G.hut} material={DARK_CONCRETE} position={[0, 0.045, 0]} />
        <mesh geometry={G.rung} material={accent} position={[0, 0.07, 0.057]} />
      </group>
      {legs.map((a, i) => {
        const b = legs[(i + 1) % legs.length];
        return (
          <group key={a}>
            <Beam
              from={at(a, foot, 0.04)}
              to={at(a, top, height)}
              material={STEEL}
              thickness={0.018}
            />
            {steps.slice(1).map((y1, j) => {
              const y0 = steps[j];
              const [p, q] = j % 2 === 0 ? [a, b] : [b, a];
              return (
                <Beam
                  key={y1}
                  from={at(p, width(y0), y0)}
                  to={at(q, width(y1), y1)}
                  material={STEEL}
                  thickness={0.008}
                />
              );
            })}
          </group>
        );
      })}
      <group position={[0, head, 0]}>
        <mesh geometry={G.head} material={GUNMETAL} />
        <mesh geometry={G.band} material={accent} position={[0, -0.015, 0]} />
        <mesh geometry={G.camera} material={DISH} position={[0, -0.05, 0.07]} />
        <mesh geometry={G.cameraLens} material={GLASS} position={[0, -0.05, 0.108]} />
        <Spin axis="y" speed={RADAR_SPEED} position={[0, 0.045, 0]}>
          <Pole length={0.04} position={[0, 0, 0]} material={GUNMETAL} />
          <mesh geometry={G.radar} material={DISH} position={[0, 0.025, 0]} />
        </Spin>
        <Pole length={0.1} position={[0, 0.09, 0]} material={STEEL} />
        <Pulse base={0.85} amount={0.25} speed={BLINK_SPEED} rectify position={[0, 0.15, 0]}>
          <mesh geometry={G.beacon} material={glow} />
          <mesh geometry={G.halo} material={translucent(glow, HALO_OPACITY)} />
        </Pulse>
        <mesh geometry={G.beacon} material={RED_LIGHT} position={[0.07, -0.02, 0]} scale={0.5} />
      </group>
    </>
  );
}

/** Watchtower look by level: entry i is level i + 1, levels past the end use the last. */
const WATCHTOWER_LEVELS = [TimberTower, StoneTower, LatticeMast] as const;

/** A watchtower: timber lookout (L1), stone tower with a telescope (L2), steel sensor mast (L3). */
export function Watchtower(props: ModelProps) {
  const Look = levelEntry(WATCHTOWER_LEVELS, props.level);
  return <Look {...props} />;
}

const SPOT = {
  ring: new THREE.TorusGeometry(0.3, 0.025, 4, 32).rotateX(Math.PI / 2),
  lid: new THREE.TorusGeometry(0.2, 0.012, 3, 24, Math.PI * 0.7).rotateX(Math.PI / 2),
  lens: new THREE.CircleGeometry(0.2, 24).rotateX(-Math.PI / 2),
  pupil: new THREE.CylinderGeometry(0.075, 0.075, 0.012, 16),
  glint: new THREE.CircleGeometry(0.025, 10).rotateX(-Math.PI / 2),
};
const SPOT_SPIN = 0.4; // radians per second
const SPOT_LENS_OPACITY = 0.25;

/**
 * The owner-only "watch spot" marker a watchtower's watchers look from: a flat
 * team-coloured lens ring on the ground with an eye in it (two lids turning
 * slowly round a glowing pupil). Sits on y = 0; the UI places and shows it.
 */
export function WatchSpotMarker({ accent, glow }: Pick<ModelProps, 'accent' | 'glow'>) {
  return (
    <group position={[0, 0.02, 0]}>
      <mesh geometry={SPOT.ring} material={accent} />
      <mesh geometry={SPOT.lens} material={translucent(glow, SPOT_LENS_OPACITY)} />
      <Spin axis="y" speed={SPOT_SPIN}>
        <mesh geometry={SPOT.lid} material={accent} />
        <mesh geometry={SPOT.lid} material={accent} rotation={[0, Math.PI, 0]} />
      </Spin>
      <mesh geometry={SPOT.pupil} material={glow} position={[0, 0.006, 0]} />
      <mesh geometry={SPOT.glint} material={DISH} position={[0.025, 0.014, -0.025]} />
    </group>
  );
}
